import { useAtomValue } from "jotai";
import {
    projectedFinancialsMetricsAtom,
    projectionsResolutionAtom,
} from "../../metrics.state";
import { useMetricClickHandler } from "../shared/useMetricClickHandler";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { Metric } from "@/entities/Finance/Finance.model";
import { applyWindowToMetrics } from "../../utils/applyWindowToMetric";
import dayjs from "dayjs";
import { HeaderWithTooltip } from "../../components/HeaderWithTooltip";

export interface SummaryTableRow {
    metric: string;
    yoyChange?: number | null;
    [key: string]: string | number | (() => void) | undefined | null;
    onClick?: () => void;
}

interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

export interface SummaryProjectedFinancialsResult {
    data: SummaryTableRow[];
    columns: PeriodColumn[];
}

function formatPeriodHeader(period: string): string {
    const date = dayjs(period);
    if (!date.isValid()) return period;

    if (period.length === 4) {
        return period;
    } else if (period.includes("Q")) {
        return period.replace("-", "-").slice(0, -2);
    } else {
        return date.format("MM-YY");
    }
}

function calculateYoYDelta(
    firstValue: number | null,
    lastValue: number | null,
): number | null {
    if (firstValue === null || lastValue === null || firstValue === 0) {
        return null;
    }
    const delta = (lastValue - firstValue) / Math.abs(firstValue);
    return delta;
}

function getMetric(
    metrics: Record<string, Metric>,
    field: string,
): Metric | undefined {
    return metrics[field];
}

export function useSummaryProjectedFinancialsData(): SummaryProjectedFinancialsResult {
    const handleMetricClick = useMetricClickHandler();
    const projectedFinancialsMetrics = useAtomValue(
        projectedFinancialsMetricsAtom,
    );
    const projectionsResolution = useAtomValue(projectionsResolutionAtom);

    const windowedMetrics = applyWindowToMetrics(
        projectedFinancialsMetrics,
        projectionsResolution.window,
    );

    const revenue = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE,
    );
    const grossMargin = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P,
    );
    const ebitda = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA,
    );
    const ebitdaMargin = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P,
    );
    const cash = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH,
    );
    const salesMarketingEfficiency = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY,
    );

    const allPeriods = new Set<string>();
    [
        revenue,
        grossMargin,
        ebitda,
        ebitdaMargin,
        cash,
        salesMarketingEfficiency,
    ].forEach((metric) => {
        metric?.values.forEach((v) => allPeriods.add(v.period));
    });

    const sortedPeriods = Array.from(allPeriods).sort();

    const periodColumns: PeriodColumn[] = sortedPeriods.map((period) => ({
        field: period,
        headerName: formatPeriodHeader(period),
    }));

    const columns: PeriodColumn[] = [
        { field: "metric", headerName: "" },
        ...periodColumns,
        {
            field: "yoyChange",
            headerName: "% Δ YoY",
            headerComponent: HeaderWithTooltip,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference from the current period over the previous period. Eg. if last 4 quarters is selected (Q4-24— Q3-25), the delta shows the difference between (Q4-24— Q3-25) and (Q4-23— Q3-24).",
            },
        },
    ];

    function createRow(
        label: string,
        metric: Metric | undefined,
        fieldType: FinanceLabFieldType,
    ): SummaryTableRow {
        const row: SummaryTableRow = {
            metric: label,
        };

        if (!metric) {
            sortedPeriods.forEach((period) => {
                row[period] = "-";
            });
            row.yoyChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            metric.values.map((v) => [v.period, v.value]),
        );

        sortedPeriods.forEach((period) => {
            const value = valuesByPeriod.get(period);
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, fieldType)
                    : "-";
        });

        const firstValue = valuesByPeriod.get(sortedPeriods[0]) ?? null;
        const lastValue =
            valuesByPeriod.get(sortedPeriods[sortedPeriods.length - 1]) ?? null;
        row.yoyChange = calculateYoYDelta(firstValue, lastValue);

        row.onClick = () => handleMetricClick(metric, label);

        return row;
    }

    const data: SummaryTableRow[] = [
        createRow("Revenue", revenue, FinanceLabFieldType.CURRENCY),
        createRow(
            "Gross Margin %",
            grossMargin,
            FinanceLabFieldType.PERCENTAGE,
        ),
        createRow("EBITDA", ebitda, FinanceLabFieldType.CURRENCY),
        createRow(
            "EBITDA Margin %",
            ebitdaMargin,
            FinanceLabFieldType.PERCENTAGE,
        ),
        createRow("Cash Balance", cash, FinanceLabFieldType.CURRENCY),
        createRow(
            "Sales & Marketing Efficiency",
            salesMarketingEfficiency,
            FinanceLabFieldType.MULTIPLE,
        ),
    ];

    return { data, columns };
}
